/**
 * Central Foundation Event Stream Hook
 *
 * Connects to Central's SSE event stream via @levelup/central-events
 * and dispatches local React Query invalidations / Redux actions
 * in response to foundation events.
 *
 * LIFECYCLE:
 * - Connects when boot is ready (workspaceId + token available)
 * - Disconnects on unmount or logout
 * - Reconnects automatically on token/workspace change (client handles backoff)
 *
 * SEPARATION:
 * - Transport: shared CentralEventStreamClient (SSE, reconnect, parsing)
 * - Reactions: local (React Query invalidation, boot reset)
 * - Notification SSE: separate (@levelup/central-notifications), not mixed here
 *
 * POLLING FALLBACK:
 * Existing React Query polling (60s) is intentionally retained as a safety net.
 * Events provide near-instant updates; polling catches anything missed.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
  CentralEventStreamClient,
  type CentralBusinessEventType,
  type CentralEventPayload,
} from '@levelup/central-events';
import { getAccessToken } from '../lib/centralAuth';
import { bootReset } from '../store/slices/bootSlice';
import type { RootState, AppDispatch } from '../store';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

/**
 * Hook that manages the Central foundation event stream lifecycle.
 *
 * Must be rendered inside BootGate (only mounts when boot status is 'ready').
 */
export function useCentralEventStream(): void {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );
  const token = useSelector((state: RootState) => state.auth.token);

  const clientRef = useRef<CentralEventStreamClient | null>(null);

  // Stable event handler — invalidates React Query caches based on event type
  const handleEvent = useCallback(
    (eventType: CentralBusinessEventType, _payload: CentralEventPayload) => {
      switch (eventType) {
        // Entitlement changes affect boot context (entitlementsEffective)
        // → re-run switch-context to get updated entitlements
        case 'entitlements.changed.v1':
          dispatch(bootReset());
          break;

        // Subscription status changes may affect entitlements and plan state
        // → re-run switch-context + refresh subscription status query
        case 'subscription.status.changed.v1':
          dispatch(bootReset());
          queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
          break;

        // Feature flag changes are carried in boot meta
        // → re-run switch-context to pick up new flags
        case 'feature.flags.changed.v1':
          dispatch(bootReset());
          break;

        // Credit balance changes — invalidate any credit-related queries
        // The credits tab and any credit-consuming UI will refetch
        case 'credits.balance.changed.v1':
          queryClient.invalidateQueries({ queryKey: ['credits'] });
          queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
          break;

        // Workspace member changes may affect current user's role/permissions
        // → re-run switch-context to get updated role
        case 'workspace.member.added.v1':
        case 'workspace.member.removed.v1':
        case 'workspace.member.updated.v1':
          dispatch(bootReset());
          break;
      }
    },
    [dispatch, queryClient],
  );

  useEffect(() => {
    const centralBase = getCentralBase();
    if (!centralBase || !workspaceId || !token) return;

    // Get raw token for Authorization header
    const accessToken = getAccessToken();
    if (!accessToken) return;

    const client = new CentralEventStreamClient({
      centralUrl: centralBase,
      workspaceId,
      accessToken,
      onEvent: handleEvent,
      onConnected: () => {
        console.log('[central-events] connected to foundation stream');
      },
      onConnectionChange: (connected) => {
        console.log('[central-events] connection:', connected ? 'up' : 'down');
      },
      onReconnect: () => {
        console.log('[central-events] reconnected');
      },
      onError: (error) => {
        console.warn('[central-events] stream error:', error.message);
      },
    });

    client.connect();
    clientRef.current = client;

    return () => {
      client.disconnect();
      clientRef.current = null;
    };
  }, [workspaceId, token, handleEvent]);
}
